<template>
	<SubmissionFetchError
		v-if="errorMessage"
		:message="errorMessage"
	/>
	<div
		v-else
		class="mt-10 flex flex-col gap-4"
	>
		<p
			class="text-secondary-900 mb-1 text-center text-base leading-normal font-bold"
		>
			{{ modalText.title }}
		</p>
		<p class="text-center text-sm font-normal text-slate-500">
			{{ modalText.subtitle }}
		</p>

		<FormKit
			v-model="email"
			type="email"
			name="email"
			:label="t('auth.email')"
			validation="required|email|differentFromLastSubmittedEmail"
			validation-visibility="live"
			:validation-rules="{ differentFromLastSubmittedEmail }"
			:validation-messages="{
				differentFromLastSubmittedEmail: modalText.validationMessage,
			}"
			autofocus
			outer-class="max-w-full"
			@node="emailNode = $event"
		/>

		<div class="flex w-full flex-col gap-y-2">
			<RegularButton
				class="bg-accent-400 text-accent-700 hover:bg-accent-500 px-5 py-2 font-bold"
				:disabled="!canResubmit || isSubmitting"
				@click="resubmit"
			>
				{{ t('home.everplansResubmit.resubmitToEverplans') }}
			</RegularButton>

			<RegularButton
				type="button"
				class="w-full justify-center rounded-md px-5 py-2 font-light text-slate-700 hover:text-slate-900"
				@click="emit('closeModal')"
			>
				{{ t('general.cancel') }}
			</RegularButton>
		</div>
	</div>
</template>

<script setup lang="ts">
import { EverplansEmailStatusEnum } from '@/classes/SubmissionStatuses/types'
import RegularButton from '@/components/forms/RegularButton.vue'
import useResubmissionHelper from '@/components/home/SubmissionStatuses/ResubmissionModals/ResubmissionHelper'
import SubmissionFetchError from '@/components/home/SubmissionStatuses/SubmissionFetchError.vue'
import { normalizeEmail } from '@/mixins/email'
import { useEverplansResubmissionService } from '@/services/everplansResubmission'
import type { FormKitNode } from '@formkit/core'
import { FormKit } from '@formkit/vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
	planId: string
	emailErrorStatus: EverplansEmailStatusEnum
	lastSubmittedEmail?: string
}>()

const { t } = useI18n()
const { updateEverplansEmail } = useEverplansResubmissionService()
const email = defineModel<string>('email')
const emailNode = ref<FormKitNode>()
const isSubmitting = ref(false)

if (props.lastSubmittedEmail) {
	email.value = props.lastSubmittedEmail
}

const emit = defineEmits(['closeModal'])

const differentFromLastSubmittedEmail = (node: FormKitNode) => {
	const normalizedLastSubmittedEmail = normalizeEmail(props.lastSubmittedEmail)

	return (
		normalizeEmail(String(node.value ?? '')) !== normalizedLastSubmittedEmail
	)
}

const canResubmit = computed(
	() => emailNode.value?.context?.state.valid ?? false
)

const { errorMessage, setError } = useResubmissionHelper()

const resubmit = async () => {
	if (!canResubmit.value || isSubmitting.value || !email.value) {
		return
	}

	isSubmitting.value = true
	try {
		await updateEverplansEmail(props.planId, email.value)
		emit('closeModal')
	} catch (e) {
		setError(e)
	}
}

// Extract this and make a shared helper with TierAndEmailForm.vue after Sales Meeting
const modalText = computed(() => {
	if (props.emailErrorStatus === EverplansEmailStatusEnum.DUPLICATE) {
		return {
			title: t('home.everplansResubmit.duplicateEmail'),
			subtitle: t('home.everplansResubmit.duplicateEmailModalSubtitle'),
			validationMessage: t(
				'home.everplansResubmit.duplicateEmailValidationMessage'
			),
		}
	}

	if (props.emailErrorStatus === EverplansEmailStatusEnum.BLANK) {
		return {
			title: t('home.everplansResubmit.blankEmailTitle'),
			subtitle: t('home.everplansResubmit.blankEmailSubtitle'),
			validationMessage: t(
				'home.everplansResubmit.blankEmailValidationMessage'
			),
		}
	}

	// Invalid is a catch-all message if we don't recognize the error.
	return {
		title: t('home.everplansResubmit.invalidEmail'),
		subtitle: t('home.everplansResubmit.invalidEmailModalSubtitle'),
		validationMessage: t(
			'home.everplansResubmit.invalidEmailValidationMessage'
		),
	}
})
</script>

<style scoped></style>
